import {
  CARD_LOAD_ERROR_KEYS,
  CARD_LOAD_FAILURE_CODES,
  CARD_LOAD_POLL_RESULT,
  CARD_LOAD_STATUS,
  CARD_LOAD_STATUS_BY_CODE,
  SCA_CHALLENGE_DECODE_FAILURE_CODE,
} from 'constants/wallet';
import { AMPLITUDE_WALLET_PROPERTY_VALUES } from 'constants/amplitude';
import { getThreeDsColorDepth } from 'utils/threeDsUtil';

// Wallet Service enums are lowercase strings on the wire; match case-insensitively against the
// canonical values so a differently-cased value still resolves.
export const findByLowerCase = (values, value) =>
  Object.values(values).find(
    candidate => candidate.toLowerCase() === String(value ?? '').toLowerCase(),
  ) ?? null;

const stripSeparators = value => String(value ?? '').replace(/[-_\s]/g, '');

// `loadStatus` is documented as int 1–4 but serialised as a lowercase string; accept both and
// return the canonical CARD_LOAD_STATUS value, or null for anything unknown.
export const normalizeCardLoadStatus = status => {
  if (status === null || status === undefined || String(status).trim() === '') {
    return null;
  }

  const numeric = Number(status);
  if (Number.isInteger(numeric)) {
    return CARD_LOAD_STATUS_BY_CODE[numeric] ?? null;
  }

  return findByLowerCase(CARD_LOAD_STATUS, stripSeparators(status));
};

// Decodes a base64 document as UTF-8 (issuer pages carry accented text). Tolerates whitespace
// and URL-safe alphabets; falls back to the raw byte string when the bytes are not valid UTF-8.
// Throws on malformed base64 so the caller can fail the load instead of polling on.
export const decodeBase64Utf8 = value => {
  const normalized = String(value)
    .replace(/\s/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const binary = atob(normalized);
  try {
    return decodeURIComponent(
      binary
        .split('')
        .map(
          character =>
            `%${character
              .charCodeAt(0)
              .toString(16)
              .padStart(2, '0')}`,
        )
        .join(''),
    );
  } catch (error) {
    return binary;
  }
};

export class ScaChallengeDecodeError extends Error {
  constructor(cause) {
    super('Card load SCA challenge document could not be decoded');
    this.name = 'ScaChallengeDecodeError';
    this.code = SCA_CHALLENGE_DECODE_FAILURE_CODE;
    this.cause = cause;
  }
}

// Turns the `sca` object of a card-load operation into what the challenge modal renders:
// `challengeHtml` arrives base64-encoded, `redirectUrl` as-is. Throws ScaChallengeDecodeError
// when the document cannot be decoded — the challenge can never be completed then.
export const extractScaChallenge = sca => {
  if (!sca || (!sca.challengeHtml && !sca.redirectUrl)) {
    return null;
  }

  let challengeHtml = null;
  if (sca.challengeHtml) {
    try {
      challengeHtml = decodeBase64Utf8(sca.challengeHtml);
    } catch (error) {
      throw new ScaChallengeDecodeError(error);
    }
  }

  return {
    type: sca.type ?? null,
    challengeHtml,
    redirectUrl: sca.redirectUrl ?? null,
  };
};

export const isSameScaChallenge = (a, b) =>
  a === b ||
  (!!a &&
    !!b &&
    a.type === b.type &&
    a.challengeHtml === b.challengeHtml &&
    a.redirectUrl === b.redirectUrl);

// Errors worth polling through: no response at all (network), throttling, or a server error.
// Any other 4xx means the operation cannot be read and polling should stop.
export const isTransientHttpError = error => {
  const status = error?.response?.status;
  if (!status) {
    return true;
  }
  return status === 429 || status >= 500;
};

// The customer's browser environment for 3-D Secure, as the card's issuer wants it: every member a
// string. Values are passed through as the browser reports them, except the colour depth, which is
// snapped to a value 3DS accepts. The Accept header cannot be read client-side, so it is left out
// (all members are optional; the issuer decides without whatever is missing).
export const getClientBrowserDetails = () => {
  const { screen: windowScreen, navigator: windowNavigator } = window;
  const javaEnabled =
    typeof windowNavigator?.javaEnabled === 'function' ? windowNavigator.javaEnabled() : false;

  return {
    browserColorDepth: getThreeDsColorDepth(windowScreen?.colorDepth),
    browserJavaEnabled: String(javaEnabled),
    browserLanguage: windowNavigator?.language ?? '',
    browserScreenHeight: String(windowScreen?.height ?? ''),
    browserScreenWidth: String(windowScreen?.width ?? ''),
    browserTimeZone: String(new Date().getTimezoneOffset()),
    browserUserAgent: windowNavigator?.userAgent ?? '',
    flag3D: 'Y',
  };
};

const WLT_CODE_PATTERN = /^WLT-\d{4}$/;

// The Wallet Service's ProblemDetails puts the code in a top-level `code` member (observed on cons,
// e.g. `"code": "WLT-5031"`); `type` is a documentation URL whose anchor repeats it. Read `code`,
// and fall back to the anchor only for a body that predates the `code` member.
const getProblemCode = error => {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') {
    return null;
  }
  if (typeof data.code === 'string' && WLT_CODE_PATTERN.test(data.code)) {
    return data.code;
  }
  const anchor = String(data.type ?? '').split('#')[1];
  return anchor && WLT_CODE_PATTERN.test(anchor) ? anchor : null;
};

// The WLT-xxxx code behind a failed load, from either the poll `failure` or a ProblemDetails body
export const getCardLoadErrorCode = error => error?.failure?.code ?? getProblemCode(error) ?? null;

// Maps a create/poll error to the translation key suffix under
// corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.error.*
export const getCardLoadErrorKey = error => {
  const code = getCardLoadErrorCode(error);
  if (code && CARD_LOAD_FAILURE_CODES[code]) {
    return CARD_LOAD_FAILURE_CODES[code];
  }

  if (error?.result === CARD_LOAD_POLL_RESULT.failed) {
    // Unknown terminal failure: the card may hold an authorization, so stay neutral about it
    return CARD_LOAD_ERROR_KEYS.notCompleted;
  }
  if (error?.result === CARD_LOAD_POLL_RESULT.pending) {
    return CARD_LOAD_ERROR_KEYS.pollingExpired;
  }
  if (error?.result === CARD_LOAD_POLL_RESULT.unreadable) {
    return CARD_LOAD_ERROR_KEYS.generic;
  }

  switch (error?.response?.status) {
    case 400:
      return CARD_LOAD_ERROR_KEYS.invalidRequest;
    case 403:
      // The role matrix refused the caller before anything was attempted, so nothing to check
      return CARD_LOAD_ERROR_KEYS.forbidden;
    case 409:
      return CARD_LOAD_ERROR_KEYS.conflict;
    case 422:
      return CARD_LOAD_ERROR_KEYS.rejected;
    default:
      return CARD_LOAD_ERROR_KEYS.generic;
  }
};

const { errorReason: ERROR_REASONS } = AMPLITUDE_WALLET_PROPERTY_VALUES;

// Analytics currency is the ISO 4217 code; the Wallet Service sends it lowercase ('gbp').
export const toAnalyticsCurrency = currency => (currency ? String(currency).toUpperCase() : null);

export const toMajorUnits = amountMinor =>
  amountMinor === null || amountMinor === undefined || Number.isNaN(Number(amountMinor))
    ? null
    : Number(amountMinor) / 100;

// Maps a failed wallet/funding request to the `errorReason` analytics enum. `fallback` is the
// reason for a server-side failure the status does not explain (e.g. bank_transfer_failed).
export const getAnalyticsErrorReason = (error, fallback = ERROR_REASONS.unknown) => {
  const status = error?.response?.status;
  if (status === 408 || status === 504 || error?.code === 'ECONNABORTED') {
    return ERROR_REASONS.gatewayTimeout;
  }
  if (status === 400 || status === 422) {
    return ERROR_REASONS.validationError;
  }
  return fallback;
};

const CARD_LOAD_ERROR_REASONS = {
  cardDeclined: ERROR_REASONS.cardDeclined,
  challengeUnavailable: ERROR_REASONS.scaAuthenticationFailed,
  pollingExpired: ERROR_REASONS.gatewayTimeout,
  invalidRequest: ERROR_REASONS.validationError,
  amountTooHigh: ERROR_REASONS.validationError,
  rejected: ERROR_REASONS.validationError,
};

// `errorKey` is the getCardLoadErrorKey result for the same error
export const getCardLoadErrorReason = errorKey =>
  CARD_LOAD_ERROR_REASONS[errorKey] ?? ERROR_REASONS.unknown;
